import { useEffect, useState } from 'react';
import {
    useOrthodontistPreferences,
    useUpdateOrthodontistPreference
} from '../../../../hooks/useOrthodontistPreferences';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { UDToggle } from '../../users/UDToggle';
import { OrthodontistProtocolsCard } from './OrthodontistProtocolsCard';

interface OrthodontistPreferencesProtocolsSectionProps {
    orthodontistId: string;
}

// Admin mirror of the self-service orthodontist app's PreferencesPanel +
// ProtocolsPanel, scoped by orthodontistId instead of the logged-in caller.
export function OrthodontistPreferencesProtocolsSection({
    orthodontistId
}: OrthodontistPreferencesProtocolsSectionProps) {
    const { showToast } = useToast();
    const { preferences, isLoading, isError, error } = useOrthodontistPreferences(orthodontistId);
    const updatePreference = useUpdateOrthodontistPreference(orthodontistId);
    const [pendingKey, setPendingKey] = useState<string | null>(null);

    useEffect(() => {
        if (isError) {
            showToast(apiErrorMessage(error, 'Failed to load preferences'), 'error');
        }
    }, [isError, error, showToast]);

    const handleToggle = async (key: string, enabled: boolean) => {
        setPendingKey(key);
        try {
            await updatePreference.mutateAsync({ key, enabled });
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to update preference'), 'error');
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <div className="flex flex-col gap-3.5 shrink-0">
            <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.06)] p-5 flex flex-col gap-5 shrink-0">
                <div>
                    <h3 className="text-sm font-bold text-gray-800">Preferences</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Treatment preferences this org manages themselves, editable here for
                        support.
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                    </div>
                ) : preferences.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">
                        No preferences defined for this organization.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {preferences.map((preference) => (
                            <div key={preference.key} className="py-3">
                                <UDToggle
                                    title={preference.title}
                                    desc={preference.description}
                                    on={preference.enabled}
                                    disabled={pendingKey === preference.key}
                                    onChange={(next) => void handleToggle(preference.key, next)}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <OrthodontistProtocolsCard orthodontistId={orthodontistId} />
        </div>
    );
}
